'use client';
import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { useForm } from 'react-hook-form';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { signIn } from '@eliadmin/core/src/api/customers/customers-api';
import type { SocialAuthProviderItem } from '@eliadmin/core/src/api/customers/customer-social-auth-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';

import { SocialAuthButtons } from '@eliadmin/core/src/components/auth/social-auth-buttons';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Form } from '@eliadmin/core/src/components/ui/form';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { zodResolver } from '@hookform/resolvers/zod';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Paragraph } from '../ui/paragraph';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { SignInDialog } from "@eliadmin/core/src/components/dialog/lazy-dialogs";
import { Heading } from '../ui/heading';

import { MdLockOutline, MdOutlineEmail } from 'react-icons/md';
import { Field } from '@eliadmin/core/src/components/form/field';

export const signInSchema = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
    email: __eliadmin_zod_1().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    password: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    'g-recaptcha-response': __eliadmin_zod_1().default(''),
  });

type SignInFormProps = {
  onSuccess?: () => void;
  initialSocialAuthProviders?: SocialAuthProviderItem[];
  inDialog?: boolean;
  onShowRegister?: () => void;
  onShowForgot?: () => void;
  className?: string;
  hideParagraph?: boolean;
  inCart?: boolean;
};

export function SignInForm({
  onSuccess,
  initialSocialAuthProviders,
  onShowRegister,
  onShowForgot,
  inDialog = false,
  className,
  hideParagraph = false,
  inCart = false,
}: SignInFormProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const locale = useLocaleStore(s => s.currentLocale);
  const { t } = useTranslations();
  const { formValidationMessages } = useFormValidationMessages(t);
  const { toast } = useToast();
  const { close } = useDialogStore();

  const currentUrl = `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

  const form = useForm<z.infer<ReturnType<typeof signInSchema>>>({
    resolver: zodResolver(signInSchema(formValidationMessages)),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const handleSubmit = form.handleSubmit(async data => {
    try {

      const resp = await signIn({ ...data,  locale });

      await setAuthToken({ authToken: resp.data.token });
      initCustomerStore({
        authToken: resp.data.token,
        customer: resp.data.customer,
      });

      toast({ title: t('text656'), variant: 'success' });
      if (onSuccess) {
        onSuccess();
        return;
      }

      if (pathname === ROUTES.CART()) {
        router.push(`${ROUTES.CART()}?cs=billing`);
        router.refresh();
      } else if (inCart) {
        router.replace(currentUrl);
        router.refresh();
      } else {
        router.replace(ROUTES.PROFILE());
      }

      if (inDialog) {
        close(SignInDialog.id);
      }
    } catch (e) {
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text594'),
        description: (e as any)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
        <CaptchaFormMessage errors={form.formState.errors} />
      <form
        onSubmit={handleSubmit}
        className={cn(
          'flex w-full flex-col items-center justify-center gap-6',
          'ea-sign-in-form',
          !inDialog && 'py-8 sm:py-12 lg:py-14',
          className,
        )}
      >
        <div className="flex w-full flex-col items-center justify-center gap-6">
          <Heading className={{ container: 'text-center' }} title={t('text190')} />
          {!hideParagraph && (
            <Paragraph size="lg" className="text-center text-[#5D5D5D]">
              {t('text193')}&nbsp;
              {/* A real button, not a clickable <span>: keyboard and screen-reader reachable. */}
              <button
                type="button"
                className={cn(
                  'cursor-pointer font-bold text-walnut transition-all ease-in-out hover:opacity-70',
                )}
                onClick={inDialog ? onShowRegister : () => router.push(ROUTES.REGISTER())}
              >
                {t('text2019')}
              </button>
            </Paragraph>
          )}
        </div>

        <SocialAuthButtons
          className="ea-sign-in-form__social-auth w-full"
          initialProviders={initialSocialAuthProviders}
        />

        <div className="ea-sign-in-form__content grid w-full grid-cols-1 gap-3 md:gap-4">
          <Field
            required
            type="email"
            name="email"
            control={form.control}
            placeholder={t('text3698')}
            before={<MdOutlineEmail size={20} className="text-[#9CA3AF]" />}
          />
          <Field
            required
            type="password"
            name="password"
            control={form.control}
            placeholder={t('text191')}
            before={<MdLockOutline size={20} className="text-[#9CA3AF]" />}
          />
        </div>

        <Button
          type="submit"
          className="ea-sign-in-form__submit-button w-full"
          isLoading={form.formState.isSubmitting}
        >
          {t('text189')}
        </Button>

        <button
          type="button"
          onClick={inDialog ? onShowForgot : () => router.push(ROUTES.PASSWORD_RESET())}
          className={cn(
            'mx-auto w-fit whitespace-nowrap text-center text-[13px] font-bold tracking-tight text-walnut transition-all hover:opacity-75 sm:text-sm',
            'custom-underline',
          )}
        >
          {t('text188')}
        </button>
      </form>
    </Form>
  );
}
